// GridLabels.tsx — Axis labels for the 3D ground-plane grid.
// X axis: numbered 1, 2, 3… on major gridlines, left to right (+X).
// Z axis: lettered A, B, C… from back (Z=0 origin) toward front (+Z).
// Construction-style circle bubbles surround each label.
// Only positive-side labels are rendered (negative X/Z unused).

import { useMemo } from 'react';
import * as THREE from 'three';
import { Text } from '@react-three/drei';

/** Convert 0-based index to spreadsheet-style letter: 0→A, 25→Z, 26→AA, 27→AB … */
function gridLetter(n: number): string {
  let label = '';
  let i = n + 1;
  while (i > 0) {
    i--;
    label = String.fromCharCode(65 + (i % 26)) + label;
    i = Math.floor(i / 26);
  }
  return label;
}

/** Snapped grid extent: 1 section of padding beyond content bounds, aligned to sectionSize. */
export interface GridExtent {
  xStart: number; xEnd: number;
  zStart: number; zEnd: number;
  cx: number; cz: number;       // center position for <Grid position={} />
  width: number; depth: number;  // args for <Grid args={[width, depth]} />
}

export function snapGridExtent(
  minX: number, maxX: number,
  minZ: number, maxZ: number,
  sectionSize: number = 600,
): GridExtent {
  const xStart = Math.floor(minX / sectionSize) * sectionSize;
  const xEnd   = Math.ceil(maxX / sectionSize) * sectionSize;
  const zStart = Math.floor(minZ / sectionSize) * sectionSize;
  const zEnd   = Math.ceil(maxZ / sectionSize) * sectionSize;
  return {
    xStart, xEnd, zStart, zEnd,
    cx: (xStart + xEnd) / 2,
    cz: (zStart + zEnd) / 2,
    width: xEnd - xStart,
    depth: zEnd - zStart,
  };
}

/** Create a filled circle (disk) lying flat on Y=0 for grid bubble. */
function makeCircleDisk(radius: number, segments = 48): THREE.BufferGeometry {
  const geo = new THREE.CircleGeometry(radius, segments);
  geo.rotateX(-Math.PI / 2);          // lay flat on ground plane
  return geo;
}

interface GridLabelsProps {
  sectionSize?: number;
  extent?: GridExtent;
  color?: string;
  fontSize?: number;
}

export default function GridLabels({
  sectionSize = 600,
  extent,
  color = '#5080b0',
  fontSize = 72,
}: GridLabelsProps) {
  const offset = sectionSize * 0.5;
  const bubbleRadius = fontSize * 0.85;
  const textColor = '#0d1117';  // matches scene background — cutout effect

  const bubbleMat = useMemo(
    () => new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide }),
    [color],
  );
  const bubbleGeo = useMemo(
    () => makeCircleDisk(bubbleRadius),
    [bubbleRadius],
  );

  const labels = useMemo(() => {
    const xLabels: { pos: [number, number, number]; text: string }[] = [];
    const zLabels: { pos: [number, number, number]; text: string }[] = [];

    if (extent) {
      let xi = 1;
      for (let x = extent.xStart; x <= extent.xEnd; x += sectionSize) {
        xLabels.push({ pos: [x, 0.5, extent.zStart - offset], text: String(xi++) });
      }
      let zi = 0;
      for (let z = extent.zStart; z <= extent.zEnd; z += sectionSize) {
        zLabels.push({ pos: [extent.xStart - offset, 0.5, z], text: gridLetter(zi++) });
      }
    } else {
      for (let i = 0; i < 21; i++) {
        const coord = i * sectionSize;
        xLabels.push({ pos: [coord, 0.5, -offset], text: String(i + 1) });
        zLabels.push({ pos: [-offset, 0.5, coord], text: gridLetter(i) });
      }
    }

    return { xLabels, zLabels };
  }, [sectionSize, extent, offset]);

  return (
    <group>
      {labels.xLabels.map((l) => (
        <group key={`x-${l.text}`} position={l.pos}>
          <mesh geometry={bubbleGeo} material={bubbleMat} position={[0, 0.1, 0]} />
          <Text
            rotation={[-Math.PI / 2, 0, 0]}
            fontSize={fontSize}
            color={textColor}
            anchorX="center"
            anchorY="middle"
            depthOffset={-1}
            position={[0, 0.2, 0]}
          >
            {l.text}
          </Text>
        </group>
      ))}
      {labels.zLabels.map((l) => (
        <group key={`z-${l.text}`} position={l.pos}>
          <mesh geometry={bubbleGeo} material={bubbleMat} position={[0, 0.1, 0]} />
          <Text
            rotation={[-Math.PI / 2, 0, 0]}
            fontSize={fontSize}
            color={textColor}
            anchorX="center"
            anchorY="middle"
            depthOffset={-1}
            position={[0, 0.2, 0]}
          >
            {l.text}
          </Text>
        </group>
      ))}
    </group>
  );
}
